import React, { useState } from 'react';
import classnames from 'classnames';
import { RichText } from 'src/components/RichText/RichText';
import { Schema } from 'src/components/Schema/Schema';

import { FaqItem } from '../CategoryLandingPage/CategoryLandingPage';

import classes from './CategoryFaq.scss';

type Props = {
    title: string;
    faq: FaqItem[];
};

const htmlEntityMap: Record<string, string> = {
    amp: '&',
    apos: "'",
    hellip: '...',
    gt: '>',
    ldquo: '"',
    lsquo: "'",
    lt: '<',
    mdash: '-',
    nbsp: ' ',
    ndash: '-',
    quot: '"',
    rdquo: '"',
    rsquo: "'",
};

const decodeHtmlEntities = (value: string): string =>
    value.replace(/&(#(\d+)|#x([\da-f]+)|[a-z]+);/gi, (match, _entity, decimal, hex) => {
        if (decimal) {
            return String.fromCodePoint(parseInt(decimal, 10));
        }

        if (hex) {
            return String.fromCodePoint(parseInt(hex, 16));
        }

        return htmlEntityMap[match.slice(1, -1).toLowerCase()] || match;
    });

export const getFaqAnswerText = (html: string): string =>
    decodeHtmlEntities(
        html
            .replace(/<(br|\/p|\/div|\/li|\/h[1-6])\b[^>]*>/gi, ' ')
            .replace(/<[^>]+>/g, '')
            .replace(/\s+/g, ' ')
            .trim(),
    )
        .replace(/\s+/g, ' ')
        .trim();

const getFaqSchemaItems = (faq: FaqItem[]) =>
    faq
        .map((item) => ({
            question: (item.question || '').trim(),
            answer: getFaqAnswerText(item.answer || ''),
        }))
        .filter((item) => item.question && item.answer);

const CategoryFaqSchema: React.FC<{ faq: FaqItem[] }> = ({ faq }) => {
    const schemaItems = getFaqSchemaItems(faq);

    if (!schemaItems.length) return null;

    return (
        <Schema
            input={{
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: schemaItems.map((item) => ({
                    '@type': 'Question',
                    name: item.question,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: item.answer,
                    },
                })),
            }}
        />
    );
};

export const CategoryFaq: React.FC<Props> = ({ title, faq }) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const handleToggle = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <div className={classes.root}>
            <CategoryFaqSchema faq={faq} />
            <h2 className={classes.title}>{title} FAQs</h2>
            <div className={classes.accordion}>
                {faq.map((item, index) => {
                    const isOpen = openIndex === index;
                    return (
                        <div
                            key={index}
                            className={classnames(classes.item, {
                                [classes.itemOpen]: isOpen,
                            })}
                        >
                            <button
                                className={classes.button}
                                onClick={() => handleToggle(index)}
                                aria-expanded={isOpen}
                                type="button"
                            >
                                <span className={classes.question}>{item.question}</span>
                                <span className={classes.icon}>
                                    <span className={classes.iconLineHorizontal} />
                                    <span
                                        className={classnames(classes.iconLineVertical, {
                                            [classes.iconLineVerticalHidden]: isOpen,
                                        })}
                                    />
                                </span>
                            </button>
                            <div
                                className={classnames(classes.panel, {
                                    [classes.panelOpen]: isOpen,
                                })}
                            >
                                <div className={classes.panelContent}>
                                    <RichText content={item.answer} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CategoryFaq;
